import React, {
  createContext, useContext, useState, useRef, ReactNode, useEffect
} from 'react';

export interface ApiCallResponse {
  status: boolean;
  message: string;
  data?: never;
}

export interface AuthenticatedUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  thumbnail?: string;
  privileges?: string[];
  homepage: {
    id: number;
    name: string;
    url: string;
  }
}

// eslint-disable-next-line no-shadow
export enum ApiMethod {
  Get = 'GET',
  Post = 'POST',
  Delete = 'DELETE',
  Patch = 'PATCH',
}

interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  accessTokenTtl: number;
  expire?: number;
}

export interface BodyParams {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  [key: string]: string | number | boolean | Date | null | any[] | undefined | BodyParams;
}

interface AuthContextType {
  isAuthenticated: boolean;
  waiting: boolean;
  login: (token: AuthResponse) => void;
  logout: () => void;
  hasPrivilege: (privilege: string | string[]) => boolean;
  apiCall:
    (path: string, method?: ApiMethod, body?: BodyParams | FormData | any | undefined) => Promise<ApiCallResponse>,
  user: AuthenticatedUser | null;
}

interface AuthProviderProps {
  children: ReactNode;
}

const ApiPathExcludeBearer: string[] = [
  'authentication',
  'password'
];

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const str: string | null = localStorage.getItem('auth');
  const savedAuth: AuthResponse | null = str ? JSON.parse(str) : null;
  const [token, setToken] = useState<AuthResponse | null>(savedAuth);
  const [authUser, setUser] = useState<AuthenticatedUser | null>(null);
  const [waiting, setWaiting] = useState<boolean>(true);
  // single-flight guard: multiple components can detect an expired token at the same time,
  // but the refresh token is single-use (rotated on the backend), so concurrent refresh
  // calls must share one in-flight request instead of racing each other
  const refreshingRef = useRef<Promise<AuthResponse | null> | null>(null);

  const login = (authToken: AuthResponse): AuthResponse => {
    // get the date number in seconds and add the token ttl
    authToken.expire = Date.now() + authToken.accessTokenTtl * 1000;
    setToken(authToken);
    localStorage.setItem('auth', JSON.stringify(authToken));
    setWaiting(true);
    return authToken;
  };

  const apiCall = async (
    path: string,
    method: ApiMethod = ApiMethod.Get,
    body: BodyParams | FormData | undefined = undefined
  ): Promise<ApiCallResponse> => {
    let newToken: AuthResponse | null = null;
    const hasBearer = !ApiPathExcludeBearer.includes(path);
    if (hasBearer && token && token.expire && (token.expire - 10 * 1000) < Date.now()) {
      // eslint-disable-next-line no-use-before-define
      newToken = await refreshToken();
    }
    const res: ApiCallResponse = { status: false, message: '' };
    try {
      const headers: Headers = new Headers();
      if (hasBearer && token) {
        headers.append('Authorization', `Bearer ${newToken ? newToken.accessToken : token.accessToken}`);
      }
      // body to query params
      if (method === ApiMethod.Get && body) {
        const params: URLSearchParams = new URLSearchParams();
        Object.keys(body).forEach((key) => params.append(key, typeof body !== 'undefined' ? (body as BodyParams)[key] as string : ''));
        path += `?${params.toString()}`;
        body = undefined;
      }
      if (body && body instanceof FormData) {
        // headers.append('Content-Type', 'multipart/form-data');
      } else {
        headers.append('Content-Type', 'application/json');
      }
      const response: Response = await fetch(`/api/${path}`, {
        method,
        headers,
        body: body ? body instanceof FormData ? body : JSON.stringify(body) : undefined
      });

      const contentDisposition = response.headers.get('Content-Disposition');
      if (contentDisposition?.startsWith('attachment;')) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = Object.assign(document.createElement('a'), {
          href: url,
          download: contentDisposition.split('filename=')[1].trim().replace(/"/g, '') || 'noname'
        });
        a.click();
        window.URL.revokeObjectURL(url);
        return res;
      }

      const data = await response.json();
      if (response.ok) {
        res.status = true;
        res.data = data;
      } else {
        if (hasBearer && response.status === 401) {
          // eslint-disable-next-line no-use-before-define
          await logout();
          return res;
        }
        if (data.message && typeof data.message === 'string') {
          res.message = data.message;
        }
        if (data.message && typeof data.message === 'object') {
          res.message = data.message.join(', ');
        }
      }
    } catch (error) {
      // todo: use Snackbar?
      res.message = 'Network error';
    }
    return res;
  };

  const logout = async () => {
    if (token) {
      await apiCall('authentication', ApiMethod.Delete, { accessToken: token.accessToken });
      setToken(null);
      localStorage.removeItem('auth');
    }
  };

  const refreshToken = (): Promise<AuthResponse | null> => {
    // reuse the in-flight refresh instead of firing a duplicate PATCH with the same
    // (single-use) refresh token, which the backend would reject as already invalidated
    if (refreshingRef.current) {
      return refreshingRef.current;
    }
    const promise = (async (): Promise<AuthResponse | null> => {
      const res: ApiCallResponse = await apiCall('authentication', ApiMethod.Patch, { refreshToken: token?.refreshToken });
      if (res.status && res.data) {
        return login(res.data);
      }
      await logout();
      return null;
    })().finally(() => {
      refreshingRef.current = null;
    });
    refreshingRef.current = promise;
    return promise;
  };

  const hasPrivilege = (privilege: string | string[]): boolean => {
    if (authUser && authUser.privileges) {
      const privileges: string[] = Array.isArray(privilege) ? privilege : [privilege];
      return privileges.some((v: string) => authUser.privileges && authUser.privileges.includes(v.toLowerCase()));
    }
    return false;
  };

  useEffect(() => {
    if (token && token.expire) {
      if ((token.expire - 10 * 1000) > Date.now()) {
        const fetchDataAsync = async () => {
          const res: ApiCallResponse = await apiCall('iam');
          if (res.status && res.data && (res.data as any).user) {
            setUser((res.data as any).user);
          }
        };
        fetchDataAsync().then(() => {
          setWaiting(false);
        });
      } else {
        const fetchDataAsync = async () => {
          await refreshToken();
        };
        fetchDataAsync().then(() => {
          setWaiting(false);
        });
      }
    } else {
      setWaiting(false);
    }
  }, [token]);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'auth' && !e.newValue) {
        setToken(null);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const user: AuthenticatedUser | null = authUser;

  return (
    <AuthContext.Provider value={{
      isAuthenticated: !!token,
      waiting,
      login,
      logout,
      apiCall,
      hasPrivilege,
      user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
